import { Canvas } from "@react-three/fiber";
import { Environment, useGLTF } from "@react-three/drei";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import * as THREE from "three";
import * as Sentry from "@sentry/react";
import { posthog } from "./lib/posthog";
import "@react-three/fiber";

import { AnimationMode, ModelConfig, ModelBounds } from "./types/models";
import {
  getModelFromQuery,
  getCustomGlbUrl,
  getAnimationModeFromQuery,
  shouldUseSceneCameraFromQuery,
  getBackgroundFromQuery,
  getAutoFocusFromQuery,
  isAdminViewFromQuery,
  isEmbedModeFromQuery,
} from "./utils/queryParams";
import { useModelConfig } from "./hooks/useModelConfig";
import { useCameraModeController } from "./hooks/useCameraModeController";
import { useCameraState } from "./hooks/useCameraState";
import { useModelBounds } from "./hooks/useModelBounds";
import { useBrandConfig } from "./hooks/useBrandConfig";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { resolveBrandTokens } from "./config/brand";
import { computeBrandTokens } from "./config/brandTokenDerivation";
import {
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_ENVIRONMENT_FILES,
  ANIMATION_MODE_CAMERAS,
} from "./constants/defaults";
import { t } from "./utils/i18n";
import { DroneModel } from "./components/Viewer/DroneModel";
import { CameraController } from "./components/Viewer/CameraController";
import { Controls } from "./components/Viewer/Controls";
import { InitialBakedCameraPose } from "./components/Viewer/InitialBakedCameraPose";
import { EnvProbe } from "./components/Viewer/EnvProbe";
import { CameraPreview } from "./components/Viewer/CameraPreview";
import { HandGestureOverlay } from "./components/Viewer/HandGestureOverlay";
import { GestureConsentDialog } from "./components/Viewer/GestureConsentDialog";
import { GestureLegend } from "./components/Viewer/GestureLegend";
import { SceneCameraBinder } from "./components/Viewer/SceneCameraBinder";
import { ModelLoadErrorBoundary } from "./components/Viewer/ModelLoadErrorBoundary";
import { LoadingScreen, LoadingScreenContainer } from "./components/UI/LoadingScreen";
import { ErrorScreen } from "./components/UI/ErrorScreen";
import { Header } from "./components/UI/Header";
import { FloatingToolbar } from "./components/UI/FloatingToolbar";
import { TopActionsMenu } from "./components/UI/TopActionsMenu";
import {
  PartsListModal as MobilePartsModal,
  ToolsListModal as MobileToolsModal,
  ViewerTutorialModal as MobileTutorialModal,
  HelpSupportModal as MobileHelpModal,
  ShareModal as MobileShareModal,
} from "./components/UI/viewer-modals";
import { useViewport } from "./hooks/useViewport";
import { TutorialModal } from "./components/UI/TutorialModal";
import { TutorialInteractionModal } from "./components/UI/TutorialInteractionModal";
import { SupportModal } from "./components/UI/SupportModal";
import { SupportChatbotModal } from "./components/UI/SupportChatbotModal";
import { SettingsModal } from "./components/UI/SettingsModal";
import { StepControls } from "./components/UI/StepControls";
import { ToolsPanel } from "./components/UI/ToolsPanel";
import { PartsPanel } from "./components/UI/PartsPanel";
import { ItemViewModal } from "./components/UI/ItemViewModal";
import { TimelineStepEditor } from "./components/UI/TimelineStepEditor";
import { StepVisibilityPanel } from "./components/UI/StepVisibilityPanel";
import { StepDataModal } from "./components/UI/StepDataModal";
import { StepVisibilityConfirmPopover } from "./components/UI/StepVisibilityConfirmPopover";
import { GlbUploadModal } from "./components/UI/GlbUploadModal";
import { injectThemeCSS } from "./utils/theme";
import { loadBundledTimelineState } from "./data/timelinePresets";
import { generateMockTools, Tool } from "./types/tools";
import { toolsService } from "./services/toolsService";
import { generateMockParts, Part } from "./types/parts";
import { partsService } from "./services/partsService";
import { translationService } from "./services/translationService";
import {
  getTutorialInteractionById,
  getTutorialSummaryItems,
} from "./data/tutorialInteractions";
import { DEFAULT_SETTINGS, ViewerSettings } from "./types/settings";
import { StepObjectVisibility, TimelineStep } from "./types/timeline";
import { upsertStepVisibilityAssignment } from "./components/Viewer/StepVisibilityInteraction";
import { useStepVisibilityFeedback } from "./hooks/useStepVisibilityFeedback";
import { useHandGestures } from "./hooks/useHandGestures";
import { useHandGestureFeatureFlag } from "./hooks/useHandGestureFeatureFlag";
import type { GestureAction } from "./types/hand-gestures";
import { getViewerRouteContext } from "./utils/viewerRouteContext";
import { useViewerManifest } from "./hooks/useViewerManifest";
import { isNetworkErrorMessage } from "./utils/networkError";
import { ViewerConfigPanel } from "./components/UI/ViewerConfigPanel";
import { useViewerProductConfig } from "./hooks/useViewerProductConfig";
import { useViewerTimeline } from "./hooks/useViewerTimeline";
import {
  useViewerAdminDeploymentRuntime,
  type ViewerAdminDeploymentRuntimeActions as AdminDeploymentRuntimeActions,
  type ViewerAdminDeploymentRuntimeState as AdminDeploymentRuntimeState,
} from "./hooks/useViewerAdminDeploymentRuntime";
import { useViewerAdminDeploymentBridgeState } from "./hooks/useViewerAdminDeploymentBridgeState";
import type { ViewerDraftSaveStatus } from "./types/viewerDeployment";
import {
  resolveViewerRuntimeAccess,
} from "./utils/viewerAccess";
import {
  fetchViewerAuthoringDraft,
  saveViewerAuthoringDraft,
} from "./Viewer/authoring/api/viewerAuthoringApi";
import { useProjectItems } from "./hooks/useProjectItems";
import { uploadImage as uploadProjectImage } from "./services/projectItemsService";
import {
  INITIAL_VIEWER_DRAFT_SYNC_STATE,
  buildViewerDraftSyncIdleState,
  buildViewerDraftSyncSavedState,
  buildViewerDraftSyncSavingState,
  resolveViewerDraftSyncFailureState,
  type ViewerDraftSyncState,
} from "./Viewer/authoring/mappers/viewerDraftSyncState";
import { resolveViewerAdminRuntimeUiState } from "./Viewer/authoring/mappers/viewerAdminRuntimeUiState";
import { resolveViewerShellBlockingState } from "./Viewer/authoring/mappers/viewerShellBlockingState";
import {
  buildTimelineStorageKey,
  loadTimelineState,
  resolveTimelineModelId,
  saveTimelineState,
} from "./utils/timelineStorage";
import { frameToTime } from "./Viewer/animation-v2/utils";
import type { GlbValidationReport } from "./services/glb-analysis";

const _bakedCameraPos = new THREE.Vector3();
const _bakedCameraForward = new THREE.Vector3();
const _bakedCameraQuat = new THREE.Quaternion();
const BAKED_CAMERA_WAYPOINT_DISTANCE = 20;

interface AdminDeploymentRuntimeBridgeProps {
  companyId: string;
  productId: string;
  expectedDraftRevision: number | null;
  saveStatus: ViewerDraftSaveStatus;
  onStateChange: (nextState: AdminDeploymentRuntimeState) => void;
  onActionsChange: (nextActions: AdminDeploymentRuntimeActions) => void;
}

function AdminDeploymentRuntimeBridge({
  companyId,
  productId,
  expectedDraftRevision,
  saveStatus,
  onStateChange,
  onActionsChange,
}: AdminDeploymentRuntimeBridgeProps) {
  const runtime = useViewerAdminDeploymentRuntime({
    enabled: true,
    companyId,
    productId,
    expectedDraftRevision,
    saveStatus,
  });

  // Keep the main viewer shell stateful, while isolating the deployment runtime
  // hook so it only mounts when the admin route can actually use it.
  useEffect(() => {
    onStateChange(runtime.state);
  }, [onStateChange, runtime.state]);

  useEffect(() => {
    onActionsChange(runtime.actions);
  }, [onActionsChange, runtime.actions]);

  return null;
}

function extractBakedCameraWaypoint(camera: THREE.Camera): {
  position: [number, number, number];
  target: [number, number, number];
} {
  camera.getWorldPosition(_bakedCameraPos);
  _bakedCameraForward
    .set(0, 0, -1)
    .applyQuaternion(camera.getWorldQuaternion(_bakedCameraQuat));

  const target = _bakedCameraPos
    .clone()
    .addScaledVector(_bakedCameraForward, BAKED_CAMERA_WAYPOINT_DISTANCE);

  return {
    position: [_bakedCameraPos.x, _bakedCameraPos.y, _bakedCameraPos.z],
    target: [target.x, target.y, target.z],
  };
}

function pruneVisibilityAssignments(
  steps: TimelineStep[],
  assignments: StepObjectVisibility[],
): StepObjectVisibility[] {
  const validStepIds = new Set(steps.map((step) => step.id));
  return assignments.filter((assignment) =>
    validStepIds.has(assignment.stepId),
  );
}

function buildAssignmentsForSelectedStepPrefix(
  steps: TimelineStep[],
  selectedStepId: string,
  objectId: string,
  included: boolean,
  existingAssignments: StepObjectVisibility[],
): StepObjectVisibility[] {
  const orderedSteps = [...steps].sort((a, b) => a.order - b.order);
  const selectedStepIndex = orderedSteps.findIndex(
    (step) => step.id === selectedStepId,
  );
  if (selectedStepIndex < 0) return existingAssignments;

  return orderedSteps
    .slice(0, selectedStepIndex + 1)
    .reduce((nextAssignments, step) => {
      return upsertStepVisibilityAssignment(nextAssignments, {
        stepId: step.id,
        objectId,
        included,
      });
    }, existingAssignments);
}

function getCurrentVisibilityRuleForObject(
  steps: TimelineStep[],
  assignments: StepObjectVisibility[],
  objectId: string,
): {
  included: boolean;
  stepName: string;
  startFrame: number;
  endFrame: number;
} | null {
  const orderedSteps = [...steps].sort((a, b) => a.order - b.order);
  let latestRule: {
    included: boolean;
    stepName: string;
    startFrame: number;
    endFrame: number;
  } | null = null;

  orderedSteps.forEach((step) => {
    const matchingAssignment = assignments.find(
      (assignment) =>
        assignment.stepId === step.id && assignment.objectId === objectId,
    );

    if (!matchingAssignment) return;

    latestRule = {
      included: matchingAssignment.included,
      stepName: step.name,
      startFrame: step.startFrame,
      endFrame: step.endFrame,
    };
  });

  return latestRule;
}

interface UploadedGlbSession {
  fileName: string;
  objectUrl: string;
  fingerprint: string;
  report: GlbValidationReport;
}

type UploadedModelLoadState =
  | { status: "idle" }
  | { status: "validated"; session: UploadedGlbSession }
  | { status: "loading"; pendingSession: UploadedGlbSession }
  | { status: "loaded"; activeSession: UploadedGlbSession }
  | {
      status: "load-error";
      pendingSession: UploadedGlbSession;
      message: string;
    };

function areUploadedSessionsEqual(
  left: UploadedGlbSession | null | undefined,
  right: UploadedGlbSession | null | undefined,
): boolean {
  if (!left || !right) return left === right;

  return (
    left.fingerprint === right.fingerprint &&
    left.objectUrl === right.objectUrl &&
    left.fileName === right.fileName
  );
}

function setUploadedLoadStateIfChanged(
  setState: Dispatch<SetStateAction<UploadedModelLoadState>>,
  nextState: UploadedModelLoadState,
) {
  setState((previousState) => {
    if (previousState.status !== nextState.status) {
      return nextState;
    }

    if (nextState.status === "idle") {
      return previousState;
    }

    if (
      nextState.status === "validated" &&
      previousState.status === "validated"
    ) {
      return areUploadedSessionsEqual(previousState.session, nextState.session)
        ? previousState
        : nextState;
    }

    if (nextState.status === "loading" && previousState.status === "loading") {
      return areUploadedSessionsEqual(
        previousState.pendingSession,
        nextState.pendingSession,
      )
        ? previousState
        : nextState;
    }

    if (nextState.status === "loaded" && previousState.status === "loaded") {
      return areUploadedSessionsEqual(
        previousState.activeSession,
        nextState.activeSession,
      )
        ? previousState
        : nextState;
    }

    if (
      nextState.status === "load-error" &&
      previousState.status === "load-error"
    ) {
      return areUploadedSessionsEqual(
        previousState.pendingSession,
        nextState.pendingSession,
      ) && previousState.message === nextState.message
        ? previousState
        : nextState;
    }

    return nextState;
  });
}

function buildUploadedModelStorageKey(session: UploadedGlbSession): string {
  return `uploaded:${session.fingerprint}`;
}

export default function DroneViewer() {
  const routeContext = getViewerRouteContext();

  // ── Instrumentation: trace viewer mount and route resolution ──
  useEffect(() => {
    posthog.capture('viewer_mount', {
      route_kind: routeContext.kind,
      company_id: routeContext.companyId,
      product_id: routeContext.productId,
      is_canonical: routeContext.isCanonical,
      pathname: window.location.pathname,
      has_preview_token: !!routeContext.previewToken,
    });
    Sentry.addBreadcrumb({
      category: 'viewer.lifecycle',
      message: 'DroneViewer mounted',
      data: {
        kind: routeContext.kind,
        companyId: routeContext.companyId,
        productId: routeContext.productId,
        isCanonical: routeContext.isCanonical,
        pathname: window.location.pathname,
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const viewerManifestState = useViewerManifest(routeContext);

  // ── Instrumentation: trace manifest state transitions ──
  useEffect(() => {
    if (viewerManifestState.status === 'error') {
      // Transient network failures (e.g. Safari "Load failed", mobile
      // connectivity drops — see NODE-EXPRESS-E) are not actionable bugs and
      // already match the ignoreErrors list in src/lib/sentry.ts. Because this
      // call site uses captureMessage with a custom string (the real cause sits
      // in `extra`), ignoreErrors does not catch it. Downgrade these to a
      // breadcrumb so they don't pollute the error stream; still report
      // genuine manifest failures (404, schema, etc.) as errors.
      const isTransientNetwork = isNetworkErrorMessage(viewerManifestState.error);

      if (isTransientNetwork) {
        Sentry.addBreadcrumb({
          category: 'viewer.manifest',
          level: 'warning',
          message: 'Viewer manifest load failed (network)',
          data: {
            viewer_route_kind: routeContext.kind,
            company_id: routeContext.companyId ?? 'null',
            product_id: routeContext.productId ?? 'null',
            error: viewerManifestState.error,
          },
        });
      } else {
        Sentry.captureMessage('Viewer manifest load failed', {
          level: 'error',
          tags: {
            viewer_route_kind: routeContext.kind,
            company_id: routeContext.companyId ?? 'null',
            product_id: routeContext.productId ?? 'null',
          },
          extra: {
            error: viewerManifestState.error,
            pathname: window.location.pathname,
          },
        });
      }

      posthog.capture('viewer_manifest_error', {
        route_kind: routeContext.kind,
        company_id: routeContext.companyId,
        product_id: routeContext.productId,
        error: viewerManifestState.error,
        transient_network: isTransientNetwork,
      });
    } else if (viewerManifestState.status === 'ready') {
      posthog.capture('viewer_manifest_loaded', {
        route_kind: routeContext.kind,
        company_id: routeContext.companyId,
        product_id: routeContext.productId,
        has_model_url: !!viewerManifestState.manifest?.modelUrl,
      });
    }
  }, [viewerManifestState.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewerManifest = viewerManifestState.manifest;
  const isEmbedMode = isEmbedModeFromQuery();
  const viewerRuntimeAccess = resolveViewerRuntimeAccess({
    route: routeContext,
    manifestStatus: viewerManifestState.status,
    manifest: viewerManifest,
    legacyAdminQueryEnabled: isAdminViewFromQuery(),
    legacyEnvAdminEnabled:
      import.meta.env.VITE_ENABLE_ADMIN_TIMELINE === "true",
  });
  const adminAccessState = viewerRuntimeAccess.adminAccessState;

  // Fetch viewer product config from API (runtime consumption)
  const viewerProductConfig = useViewerProductConfig(
    routeContext.kind === 'admin' || routeContext.kind === 'live'
      ? routeContext.companyId ?? null
      : null,
    routeContext.kind === 'admin' || routeContext.kind === 'live'
      ? routeContext.productId ?? null
      : null,
  );

  // Preserve legacy unknown-route admin rendering while centralizing the
  // access decisions in a single route/runtime resolver.
  const isAdminView = viewerRuntimeAccess.isAdminView;
  const shouldMountAdminDeploymentRuntime =
    viewerRuntimeAccess.shouldMountAdminDeploymentRuntime;
  const showAdminRuntimeUi = viewerRuntimeAccess.showAdminRuntimeUi;
  const shouldUseServerBackedDrafts =
    viewerRuntimeAccess.shouldUseServerBackedDrafts;
  const showLocalGlbUpload = showAdminRuntimeUi && !shouldUseServerBackedDrafts;

  // Fetch project items from Items API when in server-backed mode
  const projectItems = useProjectItems(
    shouldUseServerBackedDrafts ? (routeContext.companyId ?? '') : '',
    shouldUseServerBackedDrafts ? (routeContext.productId ?? '') : '',
  );

  // Fetch published timeline for live/preview routes.
  // Admin routes hydrate via fetchViewerAuthoringDraft (server-backed drafts).
  // Live/preview need this public endpoint because they have no auth and the
  // manifest DTO does not carry timeline steps.
  const publishedTimeline = useViewerTimeline(
    routeContext.kind === 'live' || routeContext.kind === 'preview'
      ? routeContext.companyId ?? null
      : null,
    routeContext.kind === 'live' || routeContext.kind === 'preview'
      ? routeContext.productId ?? null
      : null,
  );

  // Fetch brand configuration from API or use embedded defaults
  const {
    config: BRAND_CONFIG,
    isLoading: isBrandConfigLoading,
    error: brandConfigError,
  } = useBrandConfig({
    brandId: viewerManifest?.brandId ?? undefined,
  });

  // Extract brand configuration
  const { colors: baseBrandColors, typography: baseBrandTypography, companyName: baseBrandCompanyName, productName: baseBrandProductName, logoImageUrl: baseBrandLogoImageUrl } =
    BRAND_CONFIG;

  const [animationMode, setAnimationMode] = useState<AnimationMode | null>(
    null,
  );
  const [controlsTarget, setControlsTarget] = useState<
    [number, number, number]
  >([0, 0, 0]);
  const [modelReady, setModelReady] = useState(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false);
  const [isPartsPanelOpen, setIsPartsPanelOpen] = useState(false);
  // Step-instruction accordion state. Lifted to the viewer so the expanded
  // state stays consistent across breakpoint transitions and any future
  // duplicated step-control instances.
  const [isStepInstructionsExpanded, setIsStepInstructionsExpanded] = useState(true);
  const [isTimelineEditorOpen, setIsTimelineEditorOpen] = useState(false);
  const [isStepVisibilityPanelOpen, setIsStepVisibilityPanelOpen] =
    useState(false);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isGlbUploadOpen, setIsGlbUploadOpen] = useState(false);
  const [isNarrationEnabled, setIsNarrationEnabled] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobilePartsOpen, setIsMobilePartsOpen] = useState(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);
  const [isMobileTutorialOpen, setIsMobileTutorialOpen] = useState(false);
  const [isMobileHelpOpen, setIsMobileHelpOpen] = useState(false);
  const [isMobileShareOpen, setIsMobileShareOpen] = useState(false);
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const viewport = useViewport();
  const isMobileView = viewport === 'mobile';
  const [settings, setSettings] = useState<ViewerSettings>(DEFAULT_SETTINGS);
  const [runtimeModelError, setRuntimeModelError] = useState<string | null>(
    null,
  );
  const [activeTutorialInstructionId, setActiveTutorialInstructionId] =
    useState<string | null>(null);
  const [cameraTransitionConfig, setCameraTransitionConfig] = useState<{
    position: [number, number, number];
    target: [number, number, number];
    durationMs: number;
    sequenceId: number;
  } | null>(null);
  const [hasSeededBakedCameraPose, setHasSeededBakedCameraPose] =
    useState(false);
  const [tools, setTools] = useState<Tool[]>(() => generateMockTools());
  const [parts, setParts] = useState<Part[]>(() => generateMockParts());

  // When in server-backed mode, derive tools/parts from the Items API
  const effectiveTools: Tool[] = useMemo(() => {
    if (shouldUseServerBackedDrafts && projectItems.tools.length > 0) {
      return projectItems.tools.map((item) => ({
        id: item.id,
        name: item.name,
        code: item.code ?? '',
        icon: item.icon ?? 'build',
        description: item.description ?? undefined,
        category: item.category ?? undefined,
        imageUrl: item.imageUrl ?? undefined,
        available: item.available,
        metadata: item.metadata,
      }));
    }
    return tools;
  }, [shouldUseServerBackedDrafts, projectItems.tools, tools]);

  const effectiveParts: Part[] = useMemo(() => {
    if (shouldUseServerBackedDrafts && projectItems.parts.length > 0) {
      return projectItems.parts.map((item) => ({
        id: item.id,
        name: item.name,
        code: item.code ?? '',
        icon: item.icon ?? 'precision_manufacturing',
        description: item.description ?? undefined,
        category: item.category ?? undefined,
        imageUrl: item.imageUrl ?? undefined,
        available: item.available,
        metadata: item.metadata,
      }));
    }
    return parts;
  }, [shouldUseServerBackedDrafts, projectItems.parts, parts]);
  const [translatedTimelineContent, setTranslatedTimelineContent] = useState<
    Record<string, { title?: string; description?: string }>
  >({});
  const [timelineSteps, setTimelineSteps] = useState<TimelineStep[]>([]);
  const [visibilityAssignments, setVisibilityAssignments] = useState<
    StepObjectVisibility[]
  >([]);
  const [hasHydratedTimelineState, setHasHydratedTimelineState] =
    useState(false);
  const [timelineCurrentFrame, setTimelineCurrentFrame] = useState(0);
  const [timelineDurationSeconds, setTimelineDurationSeconds] = useState(60);
  const [hasStartedNarrationFlow, setHasStartedNarrationFlow] = useState(false);
  const [selectedVisibilityStepId, setSelectedVisibilityStepId] = useState<
    string | null
  >(null);
  const [stepDataModalStepId, setStepDataModalStepId] = useState<string | null>(
    null,
  );
  const [pendingVisibilityPick, setPendingVisibilityPick] = useState<{
    stepId: string;
    objectId: string;
    objectLabel: string;
    object: THREE.Object3D | null;
    pointer: { x: number; y: number };
  } | null>(null);
  const [pendingUploadedSession, setPendingUploadedSession] =
    useState<UploadedGlbSession | null>(null);
  const [activeUploadedSession, setActiveUploadedSession] =
    useState<UploadedGlbSession | null>(null);
  const [uploadedModelLoadState, setUploadedModelLoadState] =
    useState<UploadedModelLoadState>({ status: "idle" });
  const [visibilityObjectCatalog, setVisibilityObjectCatalog] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [modalState, setModalState] = useState<{
    open: boolean;
    item: Tool | Part | null;
    type: "tool" | "part" | null;
  }>({
    open: false,
    item: null,
    type: null,
  });
  const lastNarratedStepRef = useRef<string | null>(null);
  const visibilityAssignmentsRef = useRef<StepObjectVisibility[]>([]);
  const draftSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const draftRevisionRef = useRef(0);
  const [draftSyncState, setDraftSyncState] = useState<ViewerDraftSyncState>(
    INITIAL_VIEWER_DRAFT_SYNC_STATE,
  );
  const pendingUploadedSessionRef = useRef<UploadedGlbSession | null>(null);
  const activeUploadedSessionRef = useRef<UploadedGlbSession | null>(null);
  const previousStableUploadedSessionRef = useRef<UploadedGlbSession | null>(
    null,
  );
  const visibilityFeedback = useStepVisibilityFeedback();

  const { theme, componentDefaults, accentColor, typography, companyName, productName, logoImageUrl } = useMemo(
    () => {
      const viewerBrand = viewerProductConfig?.brand;
      if (viewerBrand?.accentColor) {
        const computed = computeBrandTokens(
          {
            accentColor: viewerBrand.accentColor,
            headerBackgroundColor: viewerBrand.headerBackgroundColor,
            fontFamily: viewerBrand.fontFamily,
            companyName: viewerBrand.companyName,
            productName: viewerBrand.productName,
            logoUrl: viewerBrand.logoUrl,
          },
          BRAND_CONFIG,
          settings.theme,
        );
        const resolved = resolveBrandTokens(computed, settings.theme);
        return {
          ...resolved,
          accentColor: computed.colors.accent,
          typography: computed.typography,
          companyName: computed.companyName,
          productName: computed.productName,
          logoImageUrl: computed.logoImageUrl,
        };
      }
      const resolved = resolveBrandTokens(BRAND_CONFIG, settings.theme);
      return {
        ...resolved,
        accentColor: baseBrandColors.accent,
        typography: baseBrandTypography,
        companyName: baseBrandCompanyName,
        productName: baseBrandProductName,
        logoImageUrl: baseBrandLogoImageUrl,
      };
    },
    [viewerProductConfig?.brand, settings.theme, baseBrandColors.accent, baseBrandTypography, baseBrandCompanyName, baseBrandProductName, baseBrandLogoImageUrl, BRAND_CONFIG],
  );

  // Theme tokens for viewer modals — configurable via Viewer Configuration Panel
  const viewerModalTheme = useMemo(() => ({
    backgroundColor: theme.backgroundColor,
    backdropBlur: theme.backdropBlur,
    borderColor: theme.borderColor,
    boxShadow: theme.boxShadow,
    primaryTextColor: theme.primaryTextColor,
    secondaryTextColor: theme.secondaryTextColor,
    accentColor: accentColor,
    fontFamily: typography.fontFamily,
  }), [theme, accentColor, typography.fontFamily]);

  const loadingScreenProps = useMemo(
    () => {
      // Brand-aware backdrop derived from the resolved theme + accent color so
      // the loading screen inherits the admin's Viewer Configuration. Falls
      // back to a neutral dark gradient when accent isn't parseable.
      const accent = accentColor ?? "#667eea";
      const isDark = settings.theme !== "light";
      const baseDark = "linear-gradient(135deg, #1a1a1f 0%, #2a2a30 60%, #2a2a30 100%)";
      const baseLight = "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 60%, #e5e7eb 100%)";
      const tinted = isDark
        ? `radial-gradient(circle at 30% 20%, ${accent}26 0%, transparent 55%), radial-gradient(circle at 75% 75%, ${accent}1a 0%, transparent 60%), ${baseDark}`
        : `radial-gradient(circle at 30% 20%, ${accent}1a 0%, transparent 55%), radial-gradient(circle at 75% 75%, ${accent}14 0%, transparent 60%), ${baseLight}`;

      return {
        language: settings.language,
        variant: "minimal" as const,
        logoSrc: logoImageUrl,
        // Intentionally not passing companyName: the loading screen shows
        // only the logo + "Loading 3D Model" status text. The full brand
        // identity surfaces in the header once the model is ready.
        fontFamily: typography.fontFamily,
        background: tinted,
        textColor: theme.primaryTextColor,
        mutedTextColor: theme.secondaryTextColor,
        panelBackground: theme.backgroundColor,
        panelBorderColor: theme.borderColor,
        panelBoxShadow: theme.boxShadow,
        accentColor,
      };
    },
    [
      accentColor,
      logoImageUrl,
      settings.language,
      settings.theme,
      theme.backgroundColor,
      theme.borderColor,
      theme.boxShadow,
      theme.primaryTextColor,
      theme.secondaryTextColor,
      typography.fontFamily,
    ],
  );

  const modelKey =
    routeContext.kind === "unknown"
      ? getModelFromQuery()
      : viewerManifest?.modelKey || "pergola";
  const queryCustomGlbUrl =
    routeContext.kind === "unknown"
      ? getCustomGlbUrl()
      : viewerManifest?.modelUrl || null;
  const activeCustomGlbUrl =
    activeUploadedSession?.objectUrl ?? queryCustomGlbUrl;
  const pendingCustomGlbUrl = pendingUploadedSession?.objectUrl ?? null;
  const useSceneCamera =
    routeContext.kind === "unknown" ? shouldUseSceneCameraFromQuery() : false;
  const timelineFps = 30;
  const timelineCurrentTime = frameToTime(timelineCurrentFrame, timelineFps);
  const {
    adminDeploymentRuntime,
    createPreview: createDeploymentPreview,
    deploy: deployViewerProject,
    onActionsChange: handleAdminDeploymentRuntimeActionsChange,
    onStateChange: handleAdminDeploymentRuntimeStateChange,
  } = useViewerAdminDeploymentBridgeState({
    enabled: shouldMountAdminDeploymentRuntime,
    companyId: routeContext.companyId,
    productId: routeContext.productId,
  });

  const markDraftSyncSaved = useCallback((revision: number) => {
    draftRevisionRef.current = revision;
    setDraftSyncState(buildViewerDraftSyncSavedState(revision));
  }, []);

  const markDraftSyncFailed = useCallback((error: unknown) => {
    setDraftSyncState((previousState) => ({
      ...resolveViewerDraftSyncFailureState({
        revision: previousState.revision,
        error,
      }),
    }));
  }, []);

  // Load model config
  const { modelConfig, loading, error } = useModelConfig(
    modelKey,
    activeCustomGlbUrl,
  );
  const {
    modelConfig: pendingModelConfig,
    loading: pendingLoading,
    error: pendingError,
  } = useModelConfig(
    modelKey,
    pendingCustomGlbUrl,
    Boolean(pendingUploadedSession),
  );

  const timelineModelId = useMemo(
    () =>
      resolveTimelineModelId({
        customGlbUrl: activeCustomGlbUrl,
        modelPath: modelConfig?.modelPath,
        modelKey,
        uploadedModelId: activeUploadedSession
          ? buildUploadedModelStorageKey(activeUploadedSession)
          : null,
      }),
    [
      activeCustomGlbUrl,
      activeUploadedSession,
      modelConfig?.modelPath,
      modelKey,
    ],
  );

  const timelineStorageKey = useMemo(
    () => buildTimelineStorageKey(timelineModelId),
    [timelineModelId],
  );

  // Camera state management
  const { sceneCamera, handleSceneCameraDetected, resetCameraState } =
    useCameraState();

  // Model bounds management
  const { modelBounds, handleBoundsComputed, resetBounds } = useModelBounds();

  // Baked camera is used as a suggestion system, never as the rendering camera.
  // OrbitControls stays active at all times.

  // Inject theme CSS when brand config is loaded
  useEffect(() => {
    injectThemeCSS(BRAND_CONFIG);

    // Log brand config source (helpful for debugging multi-tenant setups)
    if (brandConfigError) {
      console.warn(
        "Using embedded brand config due to error:",
        brandConfigError,
      );
    }

    // Apply initial settings
    const root = document.documentElement;
    root.setAttribute("data-theme", settings.theme);
    root.setAttribute("data-text-size", settings.textSize);
  }, [BRAND_CONFIG, settings.theme, settings.textSize, brandConfigError]);

  useEffect(() => {
    async function loadTools() {
      try {
        const response = await toolsService.fetchTools(modelKey || "default");
        setTools(response.tools);
      } catch (error) {
        console.error("Failed to load tools:", error);
      }
    }

    loadTools();
  }, [modelKey]);

  useEffect(() => {
    async function loadParts() {
      try {
        const response = await partsService.fetchParts(modelKey || "default");
        setParts(response.parts);
      } catch (error) {
        console.error("Failed to load parts:", error);
      }
    }

    loadParts();
  }, [modelKey]);

  const persistTimelineProgress = useCallback(
    (next: {
      steps: TimelineStep[];
      lastFrame: number;
      visibilityAssignments?: StepObjectVisibility[];
    }) => {
      saveTimelineState(timelineStorageKey, timelineModelId, {
        steps: next.steps,
        lastFrame: next.lastFrame,
        visibilityAssignments:
          next.visibilityAssignments ?? visibilityAssignments,
      });
    },
    [timelineModelId, timelineStorageKey, visibilityAssignments],
  );

  const applyTimelineHydrationState = useCallback(
    (next: {
      steps: TimelineStep[];
      visibilityAssignments?: StepObjectVisibility[];
      lastFrame: number;
    }) => {
      const nextAssignments = pruneVisibilityAssignments(
        next.steps,
        next.visibilityAssignments ?? [],
      );
      setTimelineSteps(next.steps);
      setVisibilityAssignments(nextAssignments);
      visibilityAssignmentsRef.current = nextAssignments;
      setTimelineCurrentFrame(next.lastFrame);
      setHasHydratedTimelineState(true);
    },
    [timelineFps],
  );

  const applyTimelineHydrationFallback = useCallback(() => {
    // Tier 0: published timeline from the live/preview release.
    // For live/preview routes, this is the source of truth published by an
    // admin via the deploy pipeline. We only consult it for those route
    // kinds — admin routes hydrate via fetchViewerAuthoringDraft elsewhere,
    // and unknown routes (legacy/embed) use the bundled preset.
    if (
      routeContext.kind === 'live' ||
      routeContext.kind === 'preview'
    ) {
      if (publishedTimeline.status === 'loading') {
        // Hold off applying any fallback while the API resolves.
        return;
      }
      if (
        publishedTimeline.status === 'ready' &&
        publishedTimeline.steps.length > 0
      ) {
        applyTimelineHydrationState({
          steps: publishedTimeline.steps,
          visibilityAssignments: publishedTimeline.visibilityAssignments,
          lastFrame: publishedTimeline.lastFrame,
        });
        return;
      }
      // 'missing' or 'error' → fall through to localStorage/bundled tiers.
    }

    const persisted = loadTimelineState(timelineStorageKey);
    if (persisted && persisted.steps.length > 0) {
      applyTimelineHydrationState({
        steps: persisted.steps,
        visibilityAssignments: persisted.visibilityAssignments ?? [],
        lastFrame: persisted.lastFrame,
      });
      return;
    }

    const bundled = loadBundledTimelineState(timelineModelId);
    if (bundled && bundled.steps.length > 0) {
      applyTimelineHydrationState({
        steps: bundled.steps,
        visibilityAssignments: bundled.visibilityAssignments ?? [],
        lastFrame: bundled.lastFrame,
      });
      return;
    }

    applyTimelineHydrationState({
      steps: [],
      visibilityAssignments: [],
      lastFrame: 0,
    });
  }, [
    applyTimelineHydrationState,
    publishedTimeline.lastFrame,
    publishedTimeline.status,
    publishedTimeline.steps,
    publishedTimeline.visibilityAssignments,
    routeContext.kind,
    timelineModelId,
    timelineStorageKey,
  ]);

  const persistServerDraft = useCallback(
    (next: {
      steps: TimelineStep[];
      lastFrame: number;
      visibilityAssignments?: StepObjectVisibility[];
    }) => {
      if (
        !shouldUseServerBackedDrafts ||
        !routeContext.companyId ||
        !routeContext.productId
      ) {
        return;
      }

      const payload = {
        expectedRevision: draftRevisionRef.current,
        timelineSteps: next.steps,
        visibilityAssignments:
          next.visibilityAssignments ?? visibilityAssignmentsRef.current,
        lastFrame: next.lastFrame,
      };

      draftSaveQueueRef.current = draftSaveQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          setDraftSyncState((previousState) =>
            buildViewerDraftSyncSavingState(previousState.revision),
          );
          const savedDraft = await saveViewerAuthoringDraft({
            companyId: routeContext.companyId!,
            productId: routeContext.productId!,
            expectedRevision: payload.expectedRevision,
            body: {
              ...payload,
            },
          });
          markDraftSyncSaved(savedDraft.revision ?? draftRevisionRef.current);
        })
        .catch((error) => {
          markDraftSyncFailed(error);
          console.error("Failed to persist viewer draft:", error);
        });
    },
    [
      markDraftSyncFailed,
      markDraftSyncSaved,
      routeContext.companyId,
      routeContext.productId,
      shouldUseServerBackedDrafts,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    setHasHydratedTimelineState(false);

    async function hydrateTimelineState() {
      if (
        !shouldUseServerBackedDrafts ||
        !routeContext.companyId ||
        !routeContext.productId
      ) {
        draftRevisionRef.current = 0;
        setDraftSyncState(buildViewerDraftSyncIdleState(0));
        applyTimelineHydrationFallback();
        return;
      }

      try {
        const draft = await fetchViewerAuthoringDraft(
          routeContext.companyId,
          routeContext.productId,
        );
        if (cancelled) return;

        if (draft) {
          markDraftSyncSaved(draft.revision ?? 0);
          if (Array.isArray(draft.tools) && draft.tools.length > 0) {
            setTools(draft.tools as Tool[]);
          }
          if (Array.isArray(draft.parts) && draft.parts.length > 0) {
            setParts(draft.parts as Part[]);
          }
          applyTimelineHydrationState({
            steps: draft.timelineSteps as TimelineStep[],
            visibilityAssignments:
              draft.visibilityAssignments as StepObjectVisibility[],
            lastFrame: draft.lastFrame,
          });
          return;
        }
      } catch (error) {
        if (!cancelled) {
          markDraftSyncFailed(error);
          console.error("Failed to load viewer draft:", error);
        }
      }

      if (!cancelled) {
        draftRevisionRef.current = 0;
        applyTimelineHydrationState({
          steps: [],
          visibilityAssignments: [],
          lastFrame: 0,
        });
      }
    }

    void hydrateTimelineState();

    return () => {
      cancelled = true;
    };
  }, [
    applyTimelineHydrationFallback,
    applyTimelineHydrationState,
    markDraftSyncFailed,
    markDraftSyncSaved,
    routeContext.companyId,
    routeContext.productId,
    shouldUseServerBackedDrafts,
  ]);

  // Reset state when model changes
  useEffect(() => {
    setModelReady(false);
    resetCameraState();
    resetBounds();
    setHasSeededBakedCameraPose(false);
    setHasStartedNarrationFlow(false);
    setIsStepVisibilityPanelOpen(false);
    setSelectedVisibilityStepId(null);
    setStepDataModalStepId(null);
    setPendingVisibilityPick(null);
    setVisibilityObjectCatalog([]);
    lastNarratedStepRef.current = null;
  }, [activeCustomGlbUrl, modelKey]);

  // Sync animation mode from query parameters
  useEffect(() => {
    const syncModeFromQuery = () =>
      setAnimationMode(getAnimationModeFromQuery());
    syncModeFromQuery();

    const handlePopState = () => syncModeFromQuery();
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const resolvedMode: AnimationMode = animationMode ?? "step_by_step";
  const animationConfig =
    modelConfig?.animations[resolvedMode] ||
    (modelConfig
      ? modelConfig.animations[modelConfig.defaultAnimation]
      : undefined);
  const cameraConfig =
    animationConfig?.camera || ANIMATION_MODE_CAMERAS.step_by_step;

  const bgColor = getBackgroundFromQuery(
    modelConfig?.background || DEFAULT_BACKGROUND_COLOR,
  );
  const autoFocus = activeCustomGlbUrl ? true : getAutoFocusFromQuery(false);

  const persistedTimelineSteps = useMemo(
    () => [...timelineSteps].sort((a, b) => a.order - b.order),
    [timelineSteps],
  );

  useEffect(() => {
    if (!hasHydratedTimelineState) return;

    setVisibilityAssignments((currentAssignments) => {
      const sourceAssignments =
        visibilityAssignmentsRef.current.length > 0
          ? visibilityAssignmentsRef.current
          : currentAssignments;
      const nextAssignments = pruneVisibilityAssignments(
        persistedTimelineSteps,
        sourceAssignments,
      );
      visibilityAssignmentsRef.current = nextAssignments;
      return nextAssignments.length === currentAssignments.length
        ? currentAssignments
        : nextAssignments;
    });
  }, [hasHydratedTimelineState, persistedTimelineSteps]);

  useEffect(() => {
    visibilityAssignmentsRef.current = visibilityAssignments;
  }, [visibilityAssignments]);

  useEffect(() => {
    pendingUploadedSessionRef.current = pendingUploadedSession;
  }, [pendingUploadedSession]);

  useEffect(() => {
    activeUploadedSessionRef.current = activeUploadedSession;
  }, [activeUploadedSession]);

  useEffect(() => {
    setRuntimeModelError(null);
  }, [activeCustomGlbUrl, modelKey]);

  useEffect(() => {
    return () => {
      const activeSession = activeUploadedSessionRef.current;
      const pendingSession = pendingUploadedSessionRef.current;

      if (activeSession?.objectUrl) {
        URL.revokeObjectURL(activeSession.objectUrl);
      }

      if (
        pendingSession?.objectUrl &&
        pendingSession.objectUrl !== activeSession?.objectUrl
      ) {
        URL.revokeObjectURL(pendingSession.objectUrl);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (persistedTimelineSteps.length === 0 || settings.language === "en") {
      setTranslatedTimelineContent({});
      return;
    }

    const translateTimelineContent = async () => {
      const translatedEntries = await Promise.all(
        persistedTimelineSteps.map(async (step) => {
          const baseTitle = step.name;
          const baseDescription = step.stepText || "";
          const [translatedTitle, translatedDescription] = await Promise.all([
            translationService.translateText(baseTitle, settings.language),
            translationService.translateText(
              baseDescription,
              settings.language,
            ),
          ]);

          return [
            step.id,
            {
              title: translatedTitle || baseTitle,
              description: translatedDescription || baseDescription,
            },
          ] as const;
        }),
      );

      if (cancelled) return;
      setTranslatedTimelineContent(Object.fromEntries(translatedEntries));
    };

    translateTimelineContent();

    return () => {
      cancelled = true;
    };
  }, [persistedTimelineSteps, settings.language]);

  const effectiveSteps = useMemo(() => {
    return persistedTimelineSteps.map((step, index) => ({
      id: step.id,
      stepNumber: index + 1,
      title: translatedTimelineContent[step.id]?.title || step.name,
      description:
        translatedTimelineContent[step.id]?.description || step.stepText,
    }));
  }, [persistedTimelineSteps, translatedTimelineContent]);

  const navigationTimelineSteps = persistedTimelineSteps;
  const selectedVisibilityStep = useMemo(
    () =>
      persistedTimelineSteps.find(
        (step) => step.id === selectedVisibilityStepId,
      ) ?? null,
    [persistedTimelineSteps, selectedVisibilityStepId],
  );
  const stepDataModalStep = useMemo(
    () =>
      persistedTimelineSteps.find((step) => step.id === stepDataModalStepId) ??
      null,
    [persistedTimelineSteps, stepDataModalStepId],
  );
  const pendingVisibilityStep = useMemo(
    () =>
      persistedTimelineSteps.find(
        (step) => step.id === pendingVisibilityPick?.stepId,
      ) ?? null,
    [pendingVisibilityPick?.stepId, persistedTimelineSteps],
  );
  const stepDataAssignedObjects = useMemo(() => {
    if (!stepDataModalStep) return [];

    const currentAssignments =
      visibilityAssignments.length > 0
        ? visibilityAssignments
        : visibilityAssignmentsRef.current;

    return currentAssignments
      .filter((assignment) => assignment.stepId === stepDataModalStep.id)
      .map((assignment) => {
        const resolvedObject = visibilityObjectCatalog.find(
          (object) => object.id === assignment.objectId,
        );
        return {
          id: assignment.objectId,
          label: resolvedObject?.label ?? assignment.objectId,
          resolved: Boolean(resolvedObject),
        };
      });
  }, [stepDataModalStep, visibilityAssignments, visibilityObjectCatalog]);
  const pendingVisibilityRule = useMemo(() => {
    if (!pendingVisibilityPick) return null;

    return getCurrentVisibilityRuleForObject(
      persistedTimelineSteps,
      visibilityAssignmentsRef.current.length > 0
        ? visibilityAssignmentsRef.current
        : visibilityAssignments,
      pendingVisibilityPick.objectId,
    );
  }, [pendingVisibilityPick, persistedTimelineSteps, visibilityAssignments]);

  const totalSteps = effectiveSteps.length;

  useEffect(() => {
    if (!modelConfig) return;
    setControlsTarget(cameraConfig.target);
  }, [modelConfig?.modelPath, cameraConfig]);

  const persistItemDraftUpdate = useCallback(
    async <T extends Tool | Part>(
      itemType: "tool" | "part",
      id: string,
      payload: {
        name: string;
        code?: string;
        category?: string;
        available: boolean;
        imageUrl?: string;
      },
      currentItem: T,
    ): Promise<Partial<T>> => {
      if (
        !shouldUseServerBackedDrafts ||
        !routeContext.companyId ||
        !routeContext.productId
      ) {
        return payload as Partial<T>;
      }

      const nextItem = {
        ...currentItem,
        ...payload,
        available: payload.available,
        imageUrl: payload.imageUrl ?? currentItem.imageUrl,
      } as T;

      const nextTools =
        itemType === "tool"
          ? tools.map((tool) => (tool.id === id ? (nextItem as Tool) : tool))
          : tools;
      const nextParts =
        itemType === "part"
          ? parts.map((part) => (part.id === id ? (nextItem as Part) : part))
          : parts;

      setDraftSyncState((previousState) =>
        buildViewerDraftSyncSavingState(previousState.revision),
      );

      try {
        const savedDraft = await saveViewerAuthoringDraft({
          companyId: routeContext.companyId,
          productId: routeContext.productId,
          expectedRevision: draftRevisionRef.current,
          body: {
            timelineSteps: persistedTimelineSteps,
            visibilityAssignments,
            lastFrame: timelineCurrentFrame,
            tools: nextTools,
            parts: nextParts,
          },
        });
        markDraftSyncSaved(savedDraft.revision ?? draftRevisionRef.current);
      } catch (error) {
        markDraftSyncFailed(error);
        throw error;
      }

      if (itemType === "tool") {
        setTools(nextTools as Tool[]);
      } else {
        setParts(nextParts as Part[]);
      }

      return payload as Partial<T>;
    },
    [
      markDraftSyncFailed,
      markDraftSyncSaved,
      parts,
      persistedTimelineSteps,
      routeContext.companyId,
      routeContext.productId,
      shouldUseServerBackedDrafts,
      timelineCurrentFrame,
      tools,
      visibilityAssignments,
    ],
  );

  const uploadProjectItemImage = useCallback(
    async (
      itemType: "tool" | "part",
      entityId: string,
      file: File,
    ): Promise<string> => {
      if (
        !routeContext.companyId ||
        !routeContext.productId
      ) {
        // Fallback to local data URL when no route context (e.g. local dev without server)
        const reader = new FileReader();
        return await new Promise<string>((resolve, reject) => {
          reader.onload = () =>
            resolve(typeof reader.result === "string" ? reader.result : "");
          reader.onerror = () =>
            reject(new Error("Failed to read image file."));
          reader.readAsDataURL(file);
        });
      }

      // Use the Items API 3-step upload flow (intent → S3 → complete)
      return uploadProjectImage(
        routeContext.companyId,
        routeContext.productId,
        entityId,
        file,
      );
    },
    [
      routeContext.companyId,
      routeContext.productId,
    ],
  );

  useEffect(() => {
    if (!autoFocus || !modelBounds) return;
    setControlsTarget([
      modelBounds.center.x,
      modelBounds.center.y,
      modelBounds.center.z,
    ]);
  }, [autoFocus, modelBounds]);

  useEffect(() => {
    if (!sceneCamera || hasSeededBakedCameraPose) return;

    const waypoint = extractBakedCameraWaypoint(sceneCamera);
    setControlsTarget(waypoint.target);
  }, [hasSeededBakedCameraPose, sceneCamera]);

  // ─── Step Navigation (single source of truth) ─────
  const stepNav = useStepNavigation({
    steps: navigationTimelineSteps,
    initialStepIndex: 0,
    currentFrame: timelineCurrentFrame,
    currentTimeSeconds: timelineCurrentTime,
    fps: timelineFps,
  });
  const cameraMode = useCameraModeController({
    hasBakedCamera: Boolean(sceneCamera),
    isPlaying: stepNav.isPlaying,
    phase: stepNav.phase,
    currentStepIndex: stepNav.currentStepIndex,
  });

  // ─── Hand Gesture Controls ─────
  const handGestureEnabled = useHandGestureFeatureFlag();
  const handGestures = useHandGestures({
    onAction: useCallback((action: GestureAction) => {
      switch (action) {
        case 'next': stepNav.next(); break;
        case 'prev': stepNav.previous(); break;
        case 'play': stepNav.play(); break;
        case 'pause': stepNav.pause(); break;
        case 'toggleInstructions': setIsPartsPanelOpen(p => !p); break;
        case 'toggleUI': break; // TODO: toggle all UI panels
      }
    }, [stepNav]),
    canGoNext: stepNav.currentStepIndex < navigationTimelineSteps.length - 1,
    canGoPrev: stepNav.currentStepIndex > 0,
  });

  // ─── Gesture UI state (consent dialog, camera preview, legend) ─────
  const [showGestureConsent, setShowGestureConsent] = useState(false);
  const [isCameraPreviewMinimized, setIsCameraPreviewMinimized] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(true);
  const [showGestureLegend, setShowGestureLegend] = useState(false);

  // Handle hands-free toggle: show consent dialog before enabling camera
  const handleHandsFreeToggle = useCallback(() => {
    if (handGestures.isActive) {
      handGestures.disable();
      setShowCameraPreview(false);
      setShowGestureLegend(false);
    } else {
      setShowGestureConsent(true);
    }
  }, [handGestures]);

  const handleGestureConsentConfirm = useCallback(() => {
    setShowGestureConsent(false);
    setShowCameraPreview(true);
    setIsCameraPreviewMinimized(false);
    setShowGestureLegend(true);
    handGestures.enable();
  }, [handGestures]);

  const handleGestureConsentCancel = useCallback(() => {
    setShowGestureConsent(false);
  }, []);

  // Derive currentStep (1-based) and totalSteps for UI consumers
  const currentStep = stepNav.currentStepNumber;
  const isPlaying = stepNav.isPlaying;
  const activeStepForDisplay = useMemo(
    () =>
      effectiveSteps.find((step) => step.stepNumber === currentStep) ?? null,
    [effectiveSteps, currentStep],
  );

  // Narrate active step text as it is shown in the step indicator.
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    if (!isNarrationEnabled) {
      window.speechSynthesis.cancel();
      return;
    }
    if (!hasStartedNarrationFlow) return;

    const activeStepId = activeStepForDisplay?.id ?? null;
    if (!activeStepId || lastNarratedStepRef.current === activeStepId) return;

    const rawText =
      activeStepForDisplay?.description || activeStepForDisplay?.title || "";
    const narration = rawText.trim();
    if (!narration) return;

    const languageMap: Record<string, string> = {
      en: "en-US",
      es: "es-ES",
      fr: "fr-FR",
      de: "de-DE",
      zh: "zh-CN",
    };

    const stepWordByLanguage: Record<string, string> = {
      en: "Step",
      es: "Paso",
      fr: "Étape",
      de: "Schritt",
      zh: "步骤",
    };

    window.speechSynthesis.cancel();

    const stepWord = stepWordByLanguage[settings.language] ?? "Step";
    const utterance = new SpeechSynthesisUtterance(
      `${stepWord} ${currentStep}. ${narration}`,
    );
    utterance.lang = languageMap[settings.language] ?? "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    window.speechSynthesis.speak(utterance);
    lastNarratedStepRef.current = activeStepId;
  }, [
    activeStepForDisplay,
    currentStep,
    hasStartedNarrationFlow,
    settings.language,
    isNarrationEnabled,
  ]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Camera transition effect — for non-baked models only.
  // When a baked camera exists, follow-mode in Controls handles per-frame tracking.
  useEffect(() => {
    if (stepNav.phase !== "playing" && stepNav.phase !== "transition") return;

    const step = stepNav.currentStep;
    if (!step) return;

    // When a baked camera exists, follow-mode tracks it every frame.
    // No one-shot transition needed — just complete the transition phase immediately.
    if (cameraMode.cameraAvailability === "baked") {
      if (stepNav.phase === "transition") {
        stepNav.onTransitionComplete();
      }
      return;
    }

    const target = step.camera?.target ?? cameraConfig.target;
    const position = step.camera?.position ?? cameraConfig.position;
    setControlsTarget(target);
    setCameraTransitionConfig({
      position,
      target,
      durationMs: 450,
      sequenceId: Date.now(),
    });
  }, [cameraMode.cameraAvailability, stepNav.currentStepIndex, stepNav.phase]);

  // Step control handlers
  const handlePrevious = () => stepNav.previous();
  const handleNext = () => {
    if (!hasStartedNarrationFlow) {
      lastNarratedStepRef.current = null;
      setHasStartedNarrationFlow(true);
    }
    stepNav.next();
  };
  const handlePlayPause = () => stepNav.togglePlayPause();
  const handleToggleNarration = () => {
    setIsNarrationEnabled((prev) => {
      const next = !prev;
      if (
        !next &&
        typeof window !== "undefined" &&
        "speechSynthesis" in window
      ) {
        window.speechSynthesis.cancel();
      }
      if (next) {
        lastNarratedStepRef.current = null;
      }
      return next;
    });
  };

  const closePanels = () => {
    setIsToolsPanelOpen(false);
    setIsPartsPanelOpen(false);
    setIsTimelineEditorOpen(false);
    setIsStepVisibilityPanelOpen(false);
    setPendingVisibilityPick(null);
  };

  const tutorialInstructions = getTutorialSummaryItems(settings.language);
  const activeTutorialDetail = getTutorialInteractionById(
    activeTutorialInstructionId,
    settings.language,
  );

  const handleViewItem = (item: Tool | Part, itemType: "tool" | "part") => {
    setModalState({ open: true, item, type: itemType });
  };

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    setTimeout(() => {
      setModalState((prev) => ({ ...prev, item: null, type: null }));
    }, 300);
  };

  const handleOrderItem = (item: Tool | Part, itemType: "tool" | "part") => {
    handleCloseModal();
  };

  const handleItemUpdated = useCallback(
    (updatedItem: Tool | Part, itemType: "tool" | "part") => {
      if (itemType === "tool") {
        setTools((prev) =>
          prev.map((tool) =>
            tool.id === updatedItem.id ? (updatedItem as Tool) : tool,
          ),
        );
      } else {
        setParts((prev) =>
          prev.map((part) =>
            part.id === updatedItem.id ? (updatedItem as Part) : part,
          ),
        );
      }

      setModalState((prev) => ({
        ...prev,
        item: updatedItem,
      }));
    },
    [],
  );

  const handleOpenSupport = () => {
    setIsSupportOpen(true);
    setIsSupportChatOpen(false);
    setIsTutorialOpen(false);
    setActiveTutorialInstructionId(null);
    closePanels();
  };

  const handleCloseSupport = () => {
    setIsSupportOpen(false);
  };

  const handleOpenSupportChat = () => {
    setIsSupportOpen(false);
    setIsSupportChatOpen(true);
  };

  const handleCloseSupportChat = () => {
    setIsSupportChatOpen(false);
  };

  const handleBackToSupport = () => {
    setIsSupportChatOpen(false);
    setIsSupportOpen(true);
  };

  const handleCallSupport = () => {
    window.location.href = "tel:+18005550123";
  };

  const handleEmailSupport = () => {
    window.location.href =
      "mailto:support@unit04manual.com?subject=Unit.04%20Support%20Request";
  };

  const handleOpenSupportChatbot = () => {
    handleOpenSupportChat();
  };

  const handleOpenProductDocumentation = () => {};

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    closePanels();
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleSettingsChange = (newSettings: ViewerSettings) => {
    setSettings(newSettings);

    // Apply theme to root element
    const root = document.documentElement;
    root.setAttribute("data-theme", newSettings.theme);

    // Apply text size
    root.setAttribute("data-text-size", newSettings.textSize);
  };

  const handleReplayTutorial = () => {
    setIsTutorialOpen(true);
  };

  const handleResetViewer = () => {
    setSettings(DEFAULT_SETTINGS);
    const root = document.documentElement;
    root.setAttribute("data-theme", DEFAULT_SETTINGS.theme);
    root.setAttribute("data-text-size", DEFAULT_SETTINGS.textSize);
    // stepNav resets when steps change; for now just pause
    stepNav.pause();
  };

  const commitUploadedSession = useCallback(
    (session: UploadedGlbSession) => {
      previousStableUploadedSessionRef.current =
        activeUploadedSessionRef.current;
      setActiveUploadedSession((previous) => {
        if (previous?.objectUrl && previous.objectUrl !== session.objectUrl) {
          URL.revokeObjectURL(previous.objectUrl);
        }

        return session;
      });

      setRuntimeModelError(null);
      stepNav.pause();
      visibilityFeedback.clear();
      resetCameraState();
      resetBounds();
      setHasSeededBakedCameraPose(false);
      setHasStartedNarrationFlow(false);
      setSelectedVisibilityStepId(null);
      setStepDataModalStepId(null);
      setPendingVisibilityPick(null);
      setVisibilityObjectCatalog([]);
      setIsToolsPanelOpen(false);
      setIsPartsPanelOpen(false);
      setIsTimelineEditorOpen(false);
      setIsStepVisibilityPanelOpen(false);
      setModalState({ open: false, item: null, type: null });
      setTimelineSteps([]);
      setVisibilityAssignments([]);
      visibilityAssignmentsRef.current = [];
      setTimelineCurrentFrame(0);
      setPendingUploadedSession(null);
      setUploadedModelLoadState({ status: "loaded", activeSession: session });
      setIsGlbUploadOpen(false);
    },
    [resetBounds, resetCameraState, stepNav, visibilityFeedback],
  );

  const handleLoadValidatedGlb = useCallback(
    (file: File, report: GlbValidationReport) => {
      const objectUrl = URL.createObjectURL(file);
      const nextSession: UploadedGlbSession = {
        fileName: file.name,
        objectUrl,
        fingerprint: report.fingerprint,
        report,
      };

      setPendingUploadedSession((previous) => {
        if (previous?.objectUrl && previous.objectUrl !== objectUrl) {
          URL.revokeObjectURL(previous.objectUrl);
        }

        return nextSession;
      });
      setRuntimeModelError(null);
      setUploadedLoadStateIfChanged(setUploadedModelLoadState, {
        status: "validated",
        session: nextSession,
      });
    },
    [],
  );

  const handleModelRuntimeError = useCallback((runtimeError: Error) => {
    const failedSession = activeUploadedSessionRef.current;
    if (failedSession) {
      const previousStableSession = previousStableUploadedSessionRef.current;

      setPendingUploadedSession(failedSession);
      setActiveUploadedSession(previousStableSession ?? null);
      setUploadedLoadStateIfChanged(setUploadedModelLoadState, {
        status: "load-error",
        pendingSession: failedSession,
        message:
          runtimeError.message ||
          "The GLB passed validation but failed during final viewer load.",
      });

      if (typeof useGLTF.clear === "function") {
        useGLTF.clear(failedSession.objectUrl);
      }

      return;
    }

    setRuntimeModelError(
      runtimeError.message || "The model failed during final viewer load.",
    );
  }, []);

  useEffect(() => {
    if (!pendingUploadedSession) return;

    if (pendingLoading) {
      setUploadedLoadStateIfChanged(setUploadedModelLoadState, {
        status: "loading",
        pendingSession: pendingUploadedSession,
      });
      return;
    }

    if (pendingError) {
      setUploadedLoadStateIfChanged(setUploadedModelLoadState, {
        status: "load-error",
        pendingSession: pendingUploadedSession,
        message:
          pendingError ||
          "The GLB passed validation but failed during final viewer load.",
      });
      return;
    }

    if (pendingModelConfig) {
      commitUploadedSession(pendingUploadedSession);
    }
  }, [
    commitUploadedSession,
    pendingError,
    pendingLoading,
    pendingModelConfig,
    pendingUploadedSession,
  ]);

  const handleSelectVisibilityStep = useCallback(
    (stepId: string) => {
      visibilityFeedback.clear();
      setSelectedVisibilityStepId(stepId);
      setPendingVisibilityPick(null);
    },
    [visibilityFeedback],
  );

  const handleViewVisibilityStepData = useCallback((stepId: string) => {
    setStepDataModalStepId(stepId);
  }, []);

  const handleCloseStepDataModal = useCallback(() => {
    setStepDataModalStepId(null);
  }, []);

  const handleVisibilityObjectPick = useCallback(
    (event: {
      objectId: string;
      objectLabel: string;
      object: THREE.Object3D | null;
      pointer: { x: number; y: number };
    }) => {
      if (!selectedVisibilityStepId) return;

      visibilityFeedback.preview(event.object);
      setPendingVisibilityPick({
        stepId: selectedVisibilityStepId,
        objectId: event.objectId,
        objectLabel: event.objectLabel,
        object: event.object,
        pointer: event.pointer,
      });
    },
    [selectedVisibilityStepId, visibilityFeedback],
  );

  const handleConfirmVisibilityAssignment = useCallback(
    (included: boolean) => {
      if (!pendingVisibilityPick) return;

      const nextAssignments = buildAssignmentsForSelectedStepPrefix(
        persistedTimelineSteps,
        pendingVisibilityPick.stepId,
        pendingVisibilityPick.objectId,
        included,
        visibilityAssignmentsRef.current,
      );

      visibilityAssignmentsRef.current = nextAssignments;
      setVisibilityAssignments(nextAssignments);
      persistTimelineProgress({
        steps: persistedTimelineSteps,
        lastFrame: timelineCurrentFrame,
        visibilityAssignments: nextAssignments,
      });
      persistServerDraft({
        steps: persistedTimelineSteps,
        lastFrame: timelineCurrentFrame,
        visibilityAssignments: nextAssignments,
      });
      visibilityFeedback.flash(pendingVisibilityPick.object, included);
      setPendingVisibilityPick(null);
    },
    [
      pendingVisibilityPick,
      persistTimelineProgress,
      persistServerDraft,
      persistedTimelineSteps,
      timelineCurrentFrame,
      visibilityFeedback,
    ],
  );

  const handleCloseVisibilityPopover = useCallback(() => {
    visibilityFeedback.clear();
    setPendingVisibilityPick(null);
  }, [visibilityFeedback]);

  const openTutorialInteraction = (instructionId: string) => {
    setActiveTutorialInstructionId(instructionId);
  };

  const closeTutorialInteraction = () => {
    setActiveTutorialInstructionId(null);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (pendingVisibilityPick) {
          handleCloseVisibilityPopover();
        } else if (stepDataModalStepId) {
          handleCloseStepDataModal();
        } else if (activeTutorialInstructionId) {
          closeTutorialInteraction();
        } else if (modalState.open) {
          handleCloseModal();
        } else if (isSupportChatOpen) {
          handleCloseSupportChat();
        } else if (isSupportOpen) {
          handleCloseSupport();
        } else if (isSettingsOpen) {
          handleCloseSettings();
        } else if (isStepVisibilityPanelOpen) {
          setIsStepVisibilityPanelOpen(false);
        } else if (isTimelineEditorOpen) {
          setIsTimelineEditorOpen(false);
        } else {
          closePanels();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    activeTutorialInstructionId,
    handleCloseStepDataModal,
    handleCloseVisibilityPopover,
    isSettingsOpen,
    isStepVisibilityPanelOpen,
    isSupportChatOpen,
    isSupportOpen,
    isTimelineEditorOpen,
    modalState.open,
    pendingVisibilityPick,
    stepDataModalStepId,
  ]);

  const adminDeploymentRuntimeBridge =
    shouldMountAdminDeploymentRuntime &&
    routeContext.companyId &&
    routeContext.productId ? (
      <AdminDeploymentRuntimeBridge
        companyId={routeContext.companyId}
        productId={routeContext.productId}
        expectedDraftRevision={draftSyncState.revision}
        saveStatus={draftSyncState.saveStatus}
        onStateChange={handleAdminDeploymentRuntimeStateChange}
        onActionsChange={handleAdminDeploymentRuntimeActionsChange}
        data-oid="7y7-kkd"
      />
    ) : null;

  const adminRuntimeUiState = resolveViewerAdminRuntimeUiState({
    shouldMountAdminDeploymentRuntime,
    adminRuntimeStatus: adminDeploymentRuntime.status,
    adminRuntimeError: adminDeploymentRuntime.error,
    deploymentError: adminDeploymentRuntime.deploymentError,
    jobStatus: adminDeploymentRuntime.jobStatus,
    draftSyncState,
  });

  const shellBlockingState = resolveViewerShellBlockingState({
    routeKind: routeContext.kind,
    manifestStatus: viewerManifestState.status,
    manifestError: viewerManifestState.error,
    adminAccessState,
    adminRuntimeUiState,
    isBrandConfigLoading,
    isModelConfigLoading: loading,
    modelConfigError: error,
    runtimeModelError,
  });

  if (shellBlockingState.reason !== "none") {
    let blockingView: React.ReactNode;

    switch (shellBlockingState.reason) {
      case "manifest_loading":
      case "admin_runtime_loading":
      case "brand_loading":
      case "model_loading": {
        const oid =
          shellBlockingState.reason === "manifest_loading"
            ? "jnpghll"
            : shellBlockingState.reason === "admin_runtime_loading"
              ? "u6ml3he"
              : shellBlockingState.reason === "brand_loading"
                ? "xd5n70n"
                : "yuy4v7o";
        // LoadingScreenContainer self-positions as fixed inset:0 with a
        // viewport-cover z-index, so no wrapping div is needed for either
        // the blocking-shell or inline-canvas mountings.
        blockingView = (
          <LoadingScreenContainer visible {...loadingScreenProps} data-oid={oid} />
        );
        break;
      }
      case "manifest_error":
        blockingView = (
          <ErrorScreen
            message={shellBlockingState.message || "Failed to load viewer manifest."}
            language={settings.language}
            data-oid="fdo1qc_"
          />
        );
        break;
      case "admin_access_denied":
        blockingView = (
          <ErrorScreen
            message={shellBlockingState.message || "Admin access denied."}
            language={settings.language}
            data-oid="8-75lbe"
          />
        );
        break;
      case "admin_runtime_error":
        blockingView = (
          <ErrorScreen
            message={shellBlockingState.message || "Failed to load deployment runtime."}
            language={settings.language}
            data-oid="l9a63_u"
          />
        );
        break;
      case "model_error":
        blockingView = (
          <ErrorScreen
            message={shellBlockingState.message || "Failed to load model configuration."}
            language={settings.language}
            data-oid="i0n-w49"
          />
        );
        break;
      case "runtime_model_error":
        blockingView = (
          <ErrorScreen
            message={shellBlockingState.message || "Model runtime failed."}
            language={settings.language}
            data-oid="p4wlgyx"
          />
        );
        break;
      default:
        blockingView = null;
        break;
    }

    return shellBlockingState.includeAdminRuntimeBridge ? (
      <>
        {adminDeploymentRuntimeBridge}
        {blockingView}
      </>
    ) : (
      <>{blockingView}</>
    );
  }

  if (!modelConfig) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fafafb",
        }}
        data-oid="g0klsr3"
      >
        <div style={{ fontSize: "18px", color: "#666" }} data-oid="s7f3z.q">
          {settings.language === "es"
            ? "No se encontr\u00f3 ninguna configuraci\u00f3n del modelo"
            : settings.language === "fr"
              ? "Aucune configuration de mod\u00e8le trouv\u00e9e"
              : settings.language === "de"
                ? "Keine Modellkonfiguration gefunden"
                : settings.language === "zh"
                  ? "\u672a\u627e\u5230\u6a21\u578b\u914d\u7f6e"
                  : "No model configuration found"}
        </div>
      </div>
    );
  }

  return (
    <>
      {adminDeploymentRuntimeBridge}
      <div
        style={{
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          background:
            "radial-gradient(circle at 50% 50%, #1a1c23 0%, #0a0b0e 100%)",
        }}
        data-name="Model Viewer"
        data-oid="e5h.se8"
      >
        {/* Mobile: floating More button + TopActionsMenu */}
        {isMobileView && modelReady && (
          <>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="ac-more-menu"
              style={{
                position: 'absolute',
                top: 'max(0.75rem, env(safe-area-inset-top, 0.75rem))',
                right: '0.75rem',
                zIndex: 120,
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(20, 20, 28, 0.8)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.9)',
                cursor: 'pointer',
                fontSize: '1.25rem',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>more_vert</span>
            </button>
            <TopActionsMenu
              open={isMobileMenuOpen}
              onOpenChange={setIsMobileMenuOpen}
              onSelect={(key) => {
                switch (key) {
                  case 'parts':
                    setIsMobilePartsOpen(true);
                    break;
                  case 'tools':
                    setIsMobileToolsOpen(true);
                    break;
                  case 'tutorial':
                    setIsMobileTutorialOpen(true);
                    break;
                  case 'help':
                    setIsMobileHelpOpen(true);
                    break;
                  case 'share':
                    setIsMobileShareOpen(true);
                    break;
                  case 'settings':
                    handleOpenSettings();
                    break;
                  case 'close':
                    window.history.back();
                    break;
                }
              }}
            />

            {/* Mobile viewer modals */}
            <MobilePartsModal
              open={isMobilePartsOpen}
              onClose={() => setIsMobilePartsOpen(false)}
              theme={viewerModalTheme}
              parts={effectiveParts.map((p) => ({
                id: p.code,
                name: p.name,
                image: p.imageUrl ?? '',
                quantity: 1,
              }))}
            />
            <MobileToolsModal
              open={isMobileToolsOpen}
              onClose={() => setIsMobileToolsOpen(false)}
              theme={viewerModalTheme}
              tools={effectiveTools.map((t) => ({
                name: t.name,
                image: t.imageUrl ?? '',
                description: t.description ?? '',
              }))}
            />
            <MobileTutorialModal
              open={isMobileTutorialOpen}
              onClose={() => setIsMobileTutorialOpen(false)}
              theme={viewerModalTheme}
              steps={[
                { title: 'Rotate', description: 'Drag to rotate the 3D model around.', animation: 'rotate' },
                { title: 'Zoom', description: 'Pinch or scroll to zoom in and out.', animation: 'zoom' },
                { title: 'Select Parts', description: 'Tap highlighted parts to see details.', animation: 'highlight' },
              ]}
            />
            <MobileHelpModal
              open={isMobileHelpOpen}
              onClose={() => setIsMobileHelpOpen(false)}
              theme={viewerModalTheme}
              contactEmail={viewerProductConfig.support?.email || undefined}
              contactPhone={viewerProductConfig.support?.phone || undefined}
              commonQuestions={(viewerProductConfig.support?.faqLinks || []).map(f => ({ label: f.label, href: f.url }))}
              onAsk={(question) => {
                console.info('[MobileHelp] Question submitted:', question);
              }}
            />
            <MobileShareModal
              open={isMobileShareOpen}
              onClose={() => setIsMobileShareOpen(false)}
              theme={viewerModalTheme}
              shareLink={typeof window !== 'undefined' ? window.location.href : ''}
              downloadUrl={viewerProductConfig.files.length > 0 ? viewerProductConfig.files[0].fileUrl : null}
              downloadFilename={viewerProductConfig.files.length > 0 ? viewerProductConfig.files[0].fileName : undefined}
            />
          </>
        )}

        {/* Desktop/Tablet: full header */}
        {!isMobileView && !isEmbedMode && (
        <Header
          onSettingsClick={handleOpenSettings}
          onCloseClick={() => {}}
          onConfigureClick={showAdminRuntimeUi ? () => setIsConfigPanelOpen(true) : undefined}
          logoSrc={logoImageUrl}
          companyName={companyName}
          productName={productName}
          language={settings.language}
          isAdminView={showAdminRuntimeUi}
          headerBackgroundColor={theme.headerBackgroundColor}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          iconColor={theme.iconColor}
          fontFamily={typography.fontFamily}
          adminActions={
            routeContext.kind === "admin" &&
            shouldMountAdminDeploymentRuntime &&
            routeContext.companyId &&
            routeContext.productId
              ? {
                  show: true,
                  previewLabel: adminDeploymentRuntime.previewLabel,
                  isPreviewPending: adminDeploymentRuntime.isPreviewPending,
                  isDeployPending: adminDeploymentRuntime.isDeployPending,
                  isPreviewDisabled: !adminDeploymentRuntime.canCreatePreview,
                  isDeployDisabled: !adminDeploymentRuntime.canDeploy,
                  statusMessage: adminRuntimeUiState.statusMessage,
                  onPreviewClick: async () => {
                    const nextWindow =
                      typeof window !== "undefined"
                        ? window.open("", "_blank", "noopener,noreferrer")
                        : null;
                    const payload = await createDeploymentPreview();
                    const previewUrl =
                      typeof payload?.previewUrl === "string"
                        ? payload.previewUrl
                        : null;

                    if (previewUrl && nextWindow) {
                      nextWindow.location.href = previewUrl;
                      return;
                    }

                    if (previewUrl && typeof window !== "undefined") {
                      window.open(previewUrl, "_blank", "noopener,noreferrer");
                      return;
                    }

                    nextWindow?.close();
                  },
                  onDeployClick: async () => {
                    await deployViewerProject();
                  },
                }
              : undefined
          }
          data-oid="i:964dy"
        />
        )}

        <div
          style={{ flex: 1, position: "relative", width: "100%" }}
          data-oid="39k5lwd"
        >
          <Canvas
            camera={{
              position: cameraConfig.position,
              fov: modelConfig.camera.fov,
              near: modelConfig.camera.near,
              far: modelConfig.camera.far,
            }}
            style={{ width: "100%", height: "100%" }}
            shadows={{ type: THREE.PCFShadowMap }}
            data-oid="9z9sdg8"
          >
            <color attach="background" args={[bgColor]} data-oid="995j21d" />
            <Environment
              files={DEFAULT_ENVIRONMENT_FILES}
              data-oid="yw-0ibn"
            />
            <EnvProbe />

            <ModelLoadErrorBoundary
              key={
                activeCustomGlbUrl ??
                modelConfig.modelPath ??
                modelKey ??
                "default-model"
              }
              onError={handleModelRuntimeError}
              data-oid="2pr3raz"
            >
              <DroneModel
                animationMode={resolvedMode}
                config={modelConfig}
                playing={stepNav.isPlaying}
                rangeEndFrame={stepNav.rangeEndFrame}
                seekTo={stepNav.seekCommand}
                fps={timelineFps}
                onFrameUpdate={setTimelineCurrentFrame}
                onDurationReady={setTimelineDurationSeconds}
                onRangeComplete={stepNav.onRangeComplete}
                onSeekConsumed={stepNav.consumeSeek}
                onBoundsComputed={handleBoundsComputed}
                onModelReady={() => setModelReady(true)}
                onSceneCameraDetected={handleSceneCameraDetected}
                visibilitySteps={navigationTimelineSteps}
                visibilityCurrentFrame={timelineCurrentFrame}
                visibilityAssignments={visibilityAssignments}
                enableStepVisibilityEditing={
                  isAdminView &&
                  isStepVisibilityPanelOpen &&
                  selectedVisibilityStep !== null
                }
                onVisibilityObjectPick={
                  showAdminRuntimeUi ? handleVisibilityObjectPick : undefined
                }
                onVisibilityObjectsReady={
                  showAdminRuntimeUi ? setVisibilityObjectCatalog : undefined
                }
                data-oid="zihmz9o"
              />
            </ModelLoadErrorBoundary>

            {/* Baked camera used as suggestion system only — never bound as rendering camera */}
            <InitialBakedCameraPose
              cameraObject={sceneCamera}
              enabled={Boolean(sceneCamera) && !hasSeededBakedCameraPose}
              onApplied={() => setHasSeededBakedCameraPose(true)}
              data-oid="dym.qap"
            />

            <SceneCameraBinder
              cameraObject={sceneCamera}
              enabled={false}
              data-oid="11pwbkg"
            />

            <CameraController
              bounds={modelBounds}
              isCustomModel={
                autoFocus && cameraMode.cameraAvailability !== "baked"
              }
              data-oid="zjwa28p"
            />

            <Controls
              target={controlsTarget}
              handoffFallbackTarget={
                modelBounds
                  ? [
                      modelBounds.center.x,
                      modelBounds.center.y,
                      modelBounds.center.z,
                    ]
                  : controlsTarget
              }
              enabled={true}
              cameraMode={cameraMode.cameraMode}
              cameraTransition={cameraTransitionConfig}
              onCameraTransitionComplete={() => {
                if (stepNav.phase === "transition") {
                  stepNav.onTransitionComplete();
                }
              }}
              onUserInteractionStart={cameraMode.onUserInteractionStart}
              followCamera={sceneCamera ?? null}
              data-oid="05cov9g"
            />

            {/* Dynamic lighting from config */}
            <ambientLight
              intensity={modelConfig.lighting.ambient}
              data-oid="_cxlmt7"
            />

            {modelConfig.lighting.directional.map((light, idx) => (
              <directionalLight
                key={idx}
                position={light.position}
                intensity={light.intensity}
                castShadow={light.castShadow}
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
                data-oid="uy1q1qw"
              />
            ))}
          </Canvas>

          {/* ─── Hand Gesture UI Overlays ───── */}
          {handGestures.isActive && showCameraPreview && handGestures.stream && (
            <CameraPreview
              stream={handGestures.stream}
              isMinimized={isCameraPreviewMinimized}
              onMinimize={() => setIsCameraPreviewMinimized(p => !p)}
              onDismiss={() => setShowCameraPreview(false)}
            />
          )}
          {handGestures.isActive && (
            <div className="fixed bottom-20 left-1/2 z-40 -translate-x-1/2">
              <HandGestureOverlay
                gesture={handGestures.currentGesture}
                confidence={handGestures.confidence}
              />
            </div>
          )}
          {handGestures.isActive && showGestureLegend && (
            <div className="fixed bottom-4 right-4 z-40">
              <GestureLegend
                isVisible={true}
                onDismiss={() => setShowGestureLegend(false)}
              />
            </div>
          )}
          <GestureConsentDialog
            open={showGestureConsent}
            onConfirm={handleGestureConsentConfirm}
            onCancel={handleGestureConsentCancel}
          />
        </div>

        <LoadingScreenContainer
          {...loadingScreenProps}
          visible={!modelReady}
          data-oid="loading-overlay-canvas"
        />

        {(isToolsPanelOpen || isPartsPanelOpen || isTimelineEditorOpen) && (
          <button
            type="button"
            aria-label={
              settings.language === "es"
                ? "Cerrar panel abierto"
                : settings.language === "fr"
                  ? "Fermer le panneau ouvert"
                  : settings.language === "de"
                    ? "Offenes Bedienfeld schlie\u00dfen"
                    : settings.language === "zh"
                      ? "\u5173\u95ed\u5df2\u6253\u5f00\u7684\u9762\u677f"
                      : "Close open panel"
            }
            onClick={closePanels}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 90,
              background: "transparent",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "default",
            }}
            data-oid="xc-d9j9"
          />
        )}

        {!isMobileView && !isEmbedMode && (
        <FloatingToolbar
          buttons={[
            {
              id: "tools",
              icon: "construction",
              title: t(settings.language, "toolbar.tools"),
              onClick: () => {
                setIsToolsPanelOpen((prev) => !prev);
                setIsPartsPanelOpen(false);
                setIsTutorialOpen(false);
                setIsSupportOpen(false);
                setIsSupportChatOpen(false);
                setIsTimelineEditorOpen(false);
                setIsStepVisibilityPanelOpen(false);
                setPendingVisibilityPick(null);
              },
            },
            {
              id: "parts",
              icon: "account_tree",
              title: t(settings.language, "toolbar.parts"),
              onClick: () => {
                setIsPartsPanelOpen((prev) => !prev);
                setIsToolsPanelOpen(false);
                setIsTutorialOpen(false);
                setIsSupportOpen(false);
                setIsSupportChatOpen(false);
                setIsTimelineEditorOpen(false);
                setIsStepVisibilityPanelOpen(false);
                setPendingVisibilityPick(null);
              },
            },
            {
              id: "sound",
              icon: isNarrationEnabled ? "volume_up" : "volume_off",
              title: isNarrationEnabled
                ? t(settings.language, "toolbar.narrationOn")
                : t(settings.language, "toolbar.narrationOff"),
              onClick: handleToggleNarration,
            },
            ...(showLocalGlbUpload
              ? [
                  {
                    id: "upload-glb",
                    icon: "upload_file",
                    title: t(settings.language, "toolbar.uploadGlb"),
                    onClick: () => {
                      setIsGlbUploadOpen(true);
                      setIsToolsPanelOpen(false);
                      setIsPartsPanelOpen(false);
                      setIsTimelineEditorOpen(false);
                      setIsStepVisibilityPanelOpen(false);
                      setIsTutorialOpen(false);
                      setIsSupportOpen(false);
                      setIsSupportChatOpen(false);
                      setPendingVisibilityPick(null);
                    },
                  },
                ]
              : []),
            ...(showAdminRuntimeUi
              ? [
                  {
                    id: "timeline",
                    icon: "timeline",
                    title: t(settings.language, "toolbar.timeline"),
                    onClick: () => {
                      setIsTimelineEditorOpen((prev) => !prev);
                      setIsToolsPanelOpen(false);
                      setIsPartsPanelOpen(false);
                      setIsStepVisibilityPanelOpen(false);
                      setIsTutorialOpen(false);
                      setIsSupportOpen(false);
                      setIsSupportChatOpen(false);
                      setPendingVisibilityPick(null);
                    },
                  },
                  {
                    id: "step-visibility",
                    icon: "visibility",
                    title: t(settings.language, "toolbar.stepVisibility"),
                    onClick: () => {
                      setIsStepVisibilityPanelOpen((prev) => !prev);
                      setIsToolsPanelOpen(false);
                      setIsPartsPanelOpen(false);
                      setIsTimelineEditorOpen(false);
                      setIsTutorialOpen(false);
                      setIsSupportOpen(false);
                      setIsSupportChatOpen(false);
                      setPendingVisibilityPick(null);
                    },
                  },
                ]
              : []),
            {
              id: "tutorial",
              icon: "school",
              title: t(settings.language, "toolbar.tutorial"),
              onClick: () => {
                setIsTutorialOpen(true);
                setIsToolsPanelOpen(false);
                setIsPartsPanelOpen(false);
                setIsTimelineEditorOpen(false);
                setIsStepVisibilityPanelOpen(false);
                setIsSupportOpen(false);
                setIsSupportChatOpen(false);
                setPendingVisibilityPick(null);
              },
            },
            ...(handGestureEnabled
              ? [
                  {
                    id: "hands-free",
                    icon: handGestures.isActive ? "back_hand" : "front_hand",
                    title: handGestures.isLoading
                      ? "Starting..."
                      : handGestures.isActive
                        ? "Hands-Free On"
                        : "Hands-Free",
                    onClick: handleHandsFreeToggle,
                  },
                ]
              : []),
          ]}
          helpButton={{
            id: "help",
            icon: "support_agent",
            title: t(settings.language, "toolbar.customerSupport"),
            onClick: handleOpenSupport,
          }}
          activeButtonId={
            isGlbUploadOpen
              ? "upload-glb"
              : handGestures.isActive
                ? "hands-free"
                : isTutorialOpen
                  ? "tutorial"
                  : isSupportOpen || isSupportChatOpen
                    ? "help"
                    : isStepVisibilityPanelOpen
                      ? "step-visibility"
                      : isTimelineEditorOpen
                        ? "timeline"
                        : isToolsPanelOpen
                          ? "tools"
                          : isPartsPanelOpen
                            ? "parts"
                            : isNarrationEnabled
                              ? "sound"
                              : undefined
          }
          iconColor={componentDefaults.toolbarIconColor}
          backgroundColor={componentDefaults.panelBackgroundColor}
          backdropBlur={componentDefaults.panelBackdropBlur}
          borderColor={componentDefaults.panelBorderColor}
          boxShadow={componentDefaults.panelBoxShadow}
          dividerColor={componentDefaults.toolbarDividerColor}
          data-oid="jbrtga2"
        />
        )}

        <ToolsPanel
          isOpen={isToolsPanelOpen}
          tools={effectiveTools}
          language={settings.language}
          onToolSelect={() => {}}
          onViewItem={handleViewItem}
          backgroundColor={componentDefaults.panelBackgroundColor}
          backdropBlur={componentDefaults.panelBackdropBlur}
          borderColor={componentDefaults.panelBorderColor}
          boxShadow={componentDefaults.panelBoxShadow}
          titleColor={componentDefaults.panelTitleColor}
          textColor={componentDefaults.panelTextColor}
          mutedTextColor={componentDefaults.panelMutedTextColor}
          iconColor={componentDefaults.panelIconColor}
          activeColor={componentDefaults.panelActiveColor}
          fontFamily={typography.fontFamily}
          data-oid="hag94pd"
        />

        {showLocalGlbUpload ? (
          <GlbUploadModal
            isOpen={isGlbUploadOpen}
            onClose={() => setIsGlbUploadOpen(false)}
            onLoadValidatedGlb={handleLoadValidatedGlb}
            language={settings.language}
            loadState={uploadedModelLoadState}
            backgroundColor={theme.backgroundColor}
            backdropBlur={theme.backdropBlur}
            borderColor={theme.borderColor}
            boxShadow={theme.boxShadow}
            primaryTextColor={theme.primaryTextColor}
            secondaryTextColor={theme.secondaryTextColor}
            accentColor={accentColor}
            fontFamily={typography.fontFamily}
            data-oid="m.:ejdr"
          />
        ) : null}

        <PartsPanel
          isOpen={isPartsPanelOpen}
          parts={effectiveParts}
          language={settings.language}
          onPartSelect={() => {}}
          onViewItem={handleViewItem}
          backgroundColor={componentDefaults.panelBackgroundColor}
          backdropBlur={componentDefaults.panelBackdropBlur}
          borderColor={componentDefaults.panelBorderColor}
          boxShadow={componentDefaults.panelBoxShadow}
          titleColor={componentDefaults.panelTitleColor}
          textColor={componentDefaults.panelTextColor}
          mutedTextColor={componentDefaults.panelMutedTextColor}
          iconColor={componentDefaults.panelIconColor}
          activeColor={componentDefaults.panelActiveColor}
          fontFamily={typography.fontFamily}
          data-oid="-i7o87x"
        />

        {showAdminRuntimeUi ? (
          <StepVisibilityPanel
            isOpen={isStepVisibilityPanelOpen}
            steps={persistedTimelineSteps}
            selectedStepId={selectedVisibilityStepId}
            language={settings.language}
            onSelectStep={handleSelectVisibilityStep}
            onViewStepData={handleViewVisibilityStepData}
            onClearAll={() => {
              setVisibilityAssignments([]);
              visibilityAssignmentsRef.current = [];
              persistTimelineProgress({
                steps: persistedTimelineSteps,
                lastFrame: timelineCurrentFrame,
                visibilityAssignments: [],
              });
              persistServerDraft({
                steps: persistedTimelineSteps,
                lastFrame: timelineCurrentFrame,
                visibilityAssignments: [],
              });
            }}
            backgroundColor={componentDefaults.panelBackgroundColor}
            backdropBlur={componentDefaults.panelBackdropBlur}
            borderColor={componentDefaults.panelBorderColor}
            boxShadow={componentDefaults.panelBoxShadow}
            titleColor={componentDefaults.panelTitleColor}
            textColor={componentDefaults.panelTextColor}
            mutedTextColor={componentDefaults.panelMutedTextColor}
            iconColor={componentDefaults.panelIconColor}
            activeColor={componentDefaults.panelActiveColor}
            fontFamily={typography.fontFamily}
            data-oid="0sdy_bt"
          />
        ) : null}

        {showAdminRuntimeUi && isTimelineEditorOpen && (
          <div
            style={{
              position: "absolute",
              left: "9.5rem",
              right: "5.5rem",
              bottom: "7rem",
              zIndex: 110,
              pointerEvents: "auto",
            }}
            data-oid="0sa-pfa"
          >
            <TimelineStepEditor
              animationDuration={timelineDurationSeconds}
              fps={timelineFps}
              currentFrame={timelineCurrentFrame}
              currentTimeSeconds={timelineCurrentTime}
              steps={timelineSteps}
              isPlaying={isPlaying}
              language={settings.language}
              onSeek={(frame) => {
                // Admin timeline scrubbing — pause and seek the mixer directly
                stepNav.seekToFrame(frame);
                setTimelineCurrentFrame(frame);
                persistTimelineProgress({
                  steps: timelineSteps,
                  lastFrame: frame,
                });
              }}
              onPlayPause={handlePlayPause}
              onStepSelect={() => {}}
              onMarkerChange={() => {}}
              onStepsChange={(nextSteps) => {
                setTimelineSteps(nextSteps);
                const nextAssignments = pruneVisibilityAssignments(
                  nextSteps,
                  visibilityAssignments,
                );
                setVisibilityAssignments(nextAssignments);
                persistTimelineProgress({
                  steps: nextSteps,
                  lastFrame: timelineCurrentFrame,
                  visibilityAssignments: nextAssignments,
                });
                persistServerDraft({
                  steps: nextSteps,
                  lastFrame: timelineCurrentFrame,
                  visibilityAssignments: nextAssignments,
                });
              }}
              data-oid="foj6guh"
            />
          </div>
        )}

        {modalState.item && modalState.type && (
          <ItemViewModal
            isOpen={modalState.open}
            onClose={handleCloseModal}
            item={modalState.item}
            itemType={modalState.type}
            onOrder={handleOrderItem}
            onSaved={handleItemUpdated}
            isAdmin={showAdminRuntimeUi}
            uploadImage={
              showAdminRuntimeUi ? uploadProjectItemImage : undefined
            }
            updateItem={
              showAdminRuntimeUi
                ? (itemType, id, payload) =>
                    persistItemDraftUpdate(
                      itemType,
                      id,
                      payload,
                      modalState.item as Tool | Part,
                    )
                : undefined
            }
            language={settings.language}
            accentColor={accentColor}
            backgroundColor={theme.backgroundColor}
            backdropBlur={theme.backdropBlur}
            borderColor={theme.borderColor}
            boxShadow={theme.boxShadow}
            primaryTextColor={theme.primaryTextColor}
            secondaryTextColor={theme.secondaryTextColor}
            fontFamily={typography.fontFamily}
            data-oid="iuld7kg"
          />
        )}

        {showAdminRuntimeUi ? (
          <StepDataModal
            isOpen={stepDataModalStep !== null}
            onClose={handleCloseStepDataModal}
            step={stepDataModalStep}
            assignedObjects={stepDataAssignedObjects}
            language={settings.language}
            backgroundColor={componentDefaults.panelBackgroundColor}
            backdropBlur={componentDefaults.panelBackdropBlur}
            borderColor={componentDefaults.panelBorderColor}
            boxShadow={componentDefaults.panelBoxShadow}
            primaryTextColor={theme.primaryTextColor}
            secondaryTextColor={theme.secondaryTextColor}
            accentColor={accentColor}
            fontFamily={typography.fontFamily}
            data-oid="fjmlzq2"
          />
        ) : null}

        {showAdminRuntimeUi ? (
          <StepVisibilityConfirmPopover
            isOpen={pendingVisibilityPick !== null}
            pointer={pendingVisibilityPick?.pointer ?? null}
            objectLabel={pendingVisibilityPick?.objectLabel ?? null}
            step={
              pendingVisibilityStep
                ? {
                    name: pendingVisibilityStep.name,
                    startFrame: pendingVisibilityStep.startFrame,
                    endFrame: pendingVisibilityStep.endFrame,
                  }
                : null
            }
            currentRule={pendingVisibilityRule}
            onInclude={() => handleConfirmVisibilityAssignment(true)}
            onExclude={() => handleConfirmVisibilityAssignment(false)}
            onClose={handleCloseVisibilityPopover}
            language={settings.language}
            backgroundColor={componentDefaults.panelBackgroundColor}
            backdropBlur={componentDefaults.panelBackdropBlur}
            borderColor={componentDefaults.panelBorderColor}
            boxShadow={componentDefaults.panelBoxShadow}
            primaryTextColor={theme.primaryTextColor}
            secondaryTextColor={theme.secondaryTextColor}
            accentColor={accentColor}
            fontFamily={typography.fontFamily}
            data-oid="nsz9asx"
          />
        ) : null}

        <TutorialModal
          isOpen={isTutorialOpen}
          onClose={() => setIsTutorialOpen(false)}
          instructions={tutorialInstructions}
          onInstructionSelect={openTutorialInteraction}
          language={settings.language}
          accentColor={accentColor}
          backgroundColor={theme.backgroundColor}
          backdropBlur={theme.backdropBlur}
          borderColor={theme.borderColor}
          boxShadow={theme.boxShadow}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          fontFamily={typography.fontFamily}
          data-oid="uijq.r1"
        />

        <TutorialInteractionModal
          isOpen={activeTutorialInstructionId !== null}
          detail={activeTutorialDetail}
          onClose={closeTutorialInteraction}
          onBack={closeTutorialInteraction}
          language={settings.language}
          accentColor={accentColor}
          backgroundColor={theme.backgroundColor}
          backdropBlur={theme.backdropBlur}
          borderColor={theme.borderColor}
          boxShadow={theme.boxShadow}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          fontFamily={typography.fontFamily}
          data-oid="f3i-tku"
        />

        <SupportModal
          isOpen={isSupportOpen}
          onClose={handleCloseSupport}
          onCallSupport={handleCallSupport}
          onEmailSupport={handleEmailSupport}
          onOpenChatbot={handleOpenSupportChatbot}
          onViewDocumentation={handleOpenProductDocumentation}
          supportEmail={viewerProductConfig.support?.email || undefined}
          supportPhone={viewerProductConfig.support?.phone || undefined}
          language={settings.language}
          accentColor={accentColor}
          backgroundColor={theme.backgroundColor}
          backdropBlur={theme.backdropBlur}
          borderColor={theme.borderColor}
          boxShadow={theme.boxShadow}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          fontFamily={typography.fontFamily}
          data-oid="2ewedl6"
        />

        <SupportChatbotModal
          isOpen={isSupportChatOpen}
          onClose={handleCloseSupportChat}
          onBack={handleBackToSupport}
          language={settings.language}
          accentColor={accentColor}
          backgroundColor={theme.backgroundColor}
          backdropBlur={theme.backdropBlur}
          borderColor={theme.borderColor}
          boxShadow={theme.boxShadow}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          fontFamily={typography.fontFamily}
          data-oid="hlqv:5u"
        />

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={handleCloseSettings}
          settings={settings}
          language={settings.language}
          themeMode={settings.theme}
          onSettingsChange={handleSettingsChange}
          onReplayTutorial={handleReplayTutorial}
          onResetViewer={handleResetViewer}
          accentColor={accentColor}
          backgroundColor={theme.backgroundColor}
          backdropBlur={theme.backdropBlur}
          borderColor={theme.borderColor}
          boxShadow={theme.boxShadow}
          primaryTextColor={theme.primaryTextColor}
          secondaryTextColor={theme.secondaryTextColor}
          fontFamily={typography.fontFamily}
          data-oid="i791xb2"
        />

        {!isEmbedMode && modelReady && <StepControls
          onPrevious={handlePrevious}
          onNext={handleNext}
          onPlayPause={handlePlayPause}
          isPlaying={isPlaying}
          language={settings.language}
          currentStep={currentStep}
          steps={effectiveSteps}
          disablePrevious={currentStep === 1}
          disableNext={currentStep === totalSteps}
          instructionsExpanded={isStepInstructionsExpanded}
          onInstructionsExpandedChange={setIsStepInstructionsExpanded}
          backgroundColor={componentDefaults.stepControlsBackgroundColor}
          backdropBlur={componentDefaults.stepControlsBackdropBlur}
          borderColor={componentDefaults.stepControlsBorderColor}
          boxShadow={componentDefaults.stepControlsBoxShadow}
          textColor={componentDefaults.stepControlsTextColor}
          iconColor={componentDefaults.stepControlsIconColor}
          primaryButtonColor={componentDefaults.stepControlsPrimaryButtonColor}
          primaryButtonTextColor={
            componentDefaults.stepControlsPrimaryButtonTextColor
          }
          progressBarColor={componentDefaults.stepControlsProgressBarColor}
          progressBarBackgroundColor={
            componentDefaults.stepControlsProgressBarBackgroundColor
          }
          fontFamily={typography.fontFamily}
          data-oid=".h5r_hk"
        />}

        {/* Admin Viewer Config Panel */}
        {showAdminRuntimeUi && routeContext.kind === 'admin' && routeContext.companyId && routeContext.productId && (
          <ViewerConfigPanel
            companyId={routeContext.companyId}
            productId={routeContext.productId}
            isOpen={isConfigPanelOpen}
            onClose={() => setIsConfigPanelOpen(false)}
            onSaveSuccess={() => {
              // Trigger a re-render of the viewer with new config
              // The runtime hook will re-fetch on next mount
            }}
          />
        )}
      </div>
    </>
  );
}
